import { useMutation, useQuery } from '@apollo/client'
import { ModalContext } from 'context/Modal'
import useLogout from 'hooks/useLogout'
import { useContext } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { colors } from 'theme'
import apolloErrorHandler from 'utils/apolloErrorHandler'

import { ACCOUNT_DELETION } from '@/graphql/mutations/profile'
import { USER } from '@/graphql/queries/user'
import {
  type AccountDeletionMutation,
  type AccountDeletionMutationVariables,
  type MeQuery,
} from '@/types/graphql'

import { Button } from '../atoms'

const contactAddress = import.meta.env.VITE_CONTACT_ADDRESS

const DeleteAccount = () => {
  const logout = useLogout()
  const { actions } = useContext(ModalContext)
  const { closeModal, setModal } = actions

  const [deleteAccount, { loading }] = useMutation<
    AccountDeletionMutation,
    AccountDeletionMutationVariables
  >(ACCOUNT_DELETION)

  const { data } = useQuery<MeQuery>(USER)

  const handleDeleteAccount = async () => {
    const userId = data?.me?.id

    if (!userId || loading) return

    closeModal()

    try {
      await deleteAccount({
        variables: { userId },
      })

      logout()
      toast.success('Votre compte a été supprimé', {
        position: 'top-center',
      })
    } catch (e) {
      apolloErrorHandler(e, 'deleteAccount')
    }
  }

  const openModal = () => {
    setModal({
      title: 'Êtes-vous vraiment sûr ?',
      details: (
        <>
          <Warning>Cette opération est irréversible !</Warning>
          {`
Vous êtes sur le point de supprimer définitivement votre compte ainsi que vos Quiz et Fiches l'Essentiel déjà disponibles. La suppression est immédiate.

Si vous n'êtes pas certain de vouloir supprimer votre compte, contactez-nous sur : `}
          <Link href={`mailto:${contactAddress}`}>{contactAddress}</Link>
          {`

`}
          <strong>Abonné via l'App Store ou le Play Store ?</strong>
          {`
Si vous avez un abonnement en cours, vous devrez le résilier et attendre sa date de fin avant de pouvoir supprimer votre compte.`}
        </>
      ),
      cancel: {
        text: 'Annuler',
        handleClick: closeModal,
      },
      validate: {
        text: 'Confirmer la suppression',
        handleClick: handleDeleteAccount,
      },
    })
  }

  return (
    <Wrapper>
      <Button variant="destructive" onClick={openModal}>
        Supprimer mon compte
      </Button>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  margin-top: 1.5rem;
`

const Warning = styled.strong`
  color: ${colors.pink};
`

const Link = styled.a`
  font-weight: bold;
  color: ${colors.lightGreen};
`

export default DeleteAccount
